// copied from apps/crm; consolidate into a shared package in S5 slice 4
// Installers sign in with their office-registered mobile; Artemis staff use Azure SSO. One auth and
// memberships decide the role. The portal only ever holds the session JWT;
// reads run as the user (RLS on org_id) and every write is a proposal.
import { createClient, type Session, type SupabaseClient } from "@supabase/supabase-js";
import { env } from "./env";
import {microsoftSignInOptions} from './auth-options';
export {microsoftSignInOptions} from './auth-options';

let client: SupabaseClient | null = null;

export function supabase(): SupabaseClient | null {
  if (env.mock) return null;
  if (!client) client = createClient(env.supabaseUrl, env.supabaseAnonKey, { auth: { persistSession: true, autoRefreshToken: true } });
  return client;
}

export async function getToken(): Promise<string | null> {
  const c = supabase();
  if (!c) return null;
  const { data } = await c.auth.getSession();
  return data.session?.access_token ?? null;
}

export async function currentSession(): Promise<Session | null> {
  const c = supabase();
  if (!c) return null;
  const { data } = await c.auth.getSession();
  return data.session;
}

/**
 * The crew's own number, in the one shape Supabase accepts: E.164.
 *
 * Jacky, 18 September: *"installer portal login should be by installer mobile number, sms to get a
 * token valid for 30 days."* An Australian mobile typed as `0412 345 678`, `+61 412 345 678` or
 * `61412345678` is the same number; without this the SMS goes nowhere and the crew sees "invalid
 * phone" for a number that is perfectly correct.
 */
export function toE164AU(input: string): string | null {
  const digits = String(input ?? "").replace(/[^\d+]/g, "");
  if (!digits) return null;
  if (digits.startsWith("+")) return /^\+\d{8,15}$/.test(digits) ? digits : null;
  if (digits.startsWith("61")) return /^61\d{9}$/.test(digits) ? `+${digits}` : null;
  if (digits.startsWith("0")) return /^0\d{9}$/.test(digits) ? `+61${digits.slice(1)}` : null;
  return /^\d{9}$/.test(digits) ? `+61${digits}` : null;
}

/** Text a one-time code to the crew's mobile. No code, no session — that is the whole check. */
export async function sendPhoneCode(phone: string): Promise<{ ok: true; phone: string } | { ok: false; error: string }> {
  const e164 = toE164AU(phone);
  if (!e164) return { ok: false, error: "That does not look like a mobile number." };
  const c = supabase();
  if (!c) return { ok: true, phone: e164 };
  // shouldCreateUser: false — an installer signs in with a number the OFFICE registered, so a
  // stranger cannot text themselves into the portal.
  const { error } = await c.auth.signInWithOtp({ phone: e164, options: { shouldCreateUser: false } });
  return error ? { ok: false, error: error.message } : { ok: true, phone: e164 };
}

/**
 * The code they read out of the SMS.
 *
 * The session this returns is what lasts: the access token is short-lived and the client refreshes
 * it silently (persistSession + autoRefreshToken below), so a crew that keeps the portal on their
 * phone stays signed in for weeks — signing out, or a revoked account, is what ends it.
 */
export async function verifyPhoneCode(phone: string, token: string): Promise<{ ok: true } | { ok: false; error: string }> {
  const e164 = toE164AU(phone);
  if (!e164) return { ok: false, error: "That does not look like a mobile number." };
  const c = supabase();
  if (!c) return { ok: true };
  const { error } = await c.auth.verifyOtp({ phone: e164, token: String(token ?? "").trim(), type: "sms" });
  return error ? { ok: false, error: error.message } : { ok: true };
}

/** Artemis staff keep the same Microsoft identity they use in CRM; no email link is sent. */
export async function signInWithMicrosoft(): Promise<{ ok: true } | { ok: false; error: string }> {
  const c = supabase();
  if (!c) return { ok: true };
  const { error } = await c.auth.signInWithOAuth(microsoftSignInOptions(window.location.href));
  return error ? { ok: false, error: error.message } : { ok: true };
}

export async function signOut(): Promise<void> {
  await supabase()?.auth.signOut();
}
